import React, { useMemo, useState } from "react";
import { Plus, Shield } from "lucide-react";
import type { VmsRole } from "../../../features/rbac/types";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { EmptyState } from "../shared/EmptyState";
import { FormModal, LabeledField, TextInput } from "../shared/FormModal";
import { RowActions } from "../shared/RowActions";
import { SearchInput } from "../shared/SearchInput";
import { SectionCard } from "../shared/SectionCard";
import { useRbacAction } from "../hooks/useRbacAction";

interface RolesTabProps {
  roles: VmsRole[];
  onCreate: (payload: { role: string; description?: string }) => void;
  onUpdate: (payload: { roleId: number; role?: string; description?: string }) => void;
  onDelete: (roleId: number) => void;
}

interface RoleFormState {
  role: string;
  description: string;
}

const emptyForm: RoleFormState = { role: "", description: "" };

export const RolesTab: React.FC<RolesTabProps> = ({ roles, onCreate, onUpdate, onDelete }) => {
  const { matches, isBusy } = useRbacAction();
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState<VmsRole | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<RoleFormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<VmsRole | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.role.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        String(r.roleId).includes(q)
    );
  }, [roles, search]);

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const openEdit = (role: VmsRole) => {
    setEditTarget(role);
    setForm({ role: role.role, description: role.description ?? "" });
  };

  const closeForm = () => {
    setCreateOpen(false);
    setEditTarget(null);
    setForm(emptyForm);
  };

  const trimmed = {
    role: form.role.trim(),
    description: form.description.trim(),
  };

  const submitDisabled = !trimmed.role;

  const handleSubmit = () => {
    if (submitDisabled) return;
    if (editTarget) {
      onUpdate({
        roleId: editTarget.roleId,
        role: trimmed.role !== editTarget.role ? trimmed.role : undefined,
        description:
          trimmed.description !== (editTarget.description ?? "")
            ? trimmed.description
            : undefined,
      });
    } else {
      onCreate(
        trimmed.description ? { role: trimmed.role, description: trimmed.description } : { role: trimmed.role }
      );
    }
    closeForm();
  };

  return (
    <SectionCard
      title="Roles"
      description="Top-level VMS roles. Used during login and as the parent of role-resources."
      actions={
        <>
          <SearchInput value={search} onChange={setSearch} placeholder="Search roles" />
          <button
            type="button"
            onClick={openCreate}
            disabled={isBusy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            New role
          </button>
        </>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Shield className="h-5 w-5" />}
          title={search ? "No roles match your search" : "No roles yet"}
          description={
            search ? "Try a different search term." : "Create your first VMS role to get started."
          }
          action={
            !search && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90"
              >
                <Plus className="h-4 w-4" />
                New role
              </button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((role) => (
                <tr key={role.roleId} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{role.roleId}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{role.role}</td>
                  <td className="px-4 py-3 text-gray-600">{role.description || "—"}</td>
                  <td className="px-4 py-3">
                    <RowActions
                      onEdit={() => openEdit(role)}
                      onDelete={() => setDeleteTarget(role)}
                      editing={matches("updateVmsRole", role.roleId)}
                      deleting={matches("deleteVmsRole", role.roleId)}
                      disabled={isBusy}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={createOpen || editTarget != null}
        title={editTarget ? `Edit role · ${editTarget.role}` : "Create new role"}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={
          editTarget
            ? matches("updateVmsRole", editTarget.roleId)
            : matches("createVmsRole")
        }
        submitDisabled={submitDisabled}
        submitLabel={editTarget ? "Save changes" : "Create role"}
      >
        <LabeledField label="Role name" required>
          <TextInput
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            placeholder="e.g. manager"
            autoFocus
          />
        </LabeledField>
        <LabeledField label="Description">
          <TextInput
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Short description"
          />
        </LabeledField>
      </FormModal>

      <ConfirmDialog
        open={deleteTarget != null}
        title={`Delete role "${deleteTarget?.role ?? ""}"?`}
        description={
          <>
            This action permanently removes the VMS role and may affect users that depend on it.
          </>
        }
        confirmLabel="Delete role"
        destructive
        loading={deleteTarget ? matches("deleteVmsRole", deleteTarget.roleId) : false}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.roleId);
            setDeleteTarget(null);
          }
        }}
      />
    </SectionCard>
  );
};
